import type { AccesoDeInicio, Navegacion } from '../../api/operaciones';
import { tituloGrupo } from '../../logica/navegacion';

/**
 * «Cambiar accesos» de Inicio (G129; componentes.md §12d, RV Accesos en modo edición), sin estilos. Las seis casillas son
 * `home_shortcuts`; el cajón ofrece los `shortcuts` que la persona puede usar (no los `feature`, G800): primero «Sugeridos
 * para ti» (hasta 4 que aún no tiene, por `order`) y después por la categoría del menú de su módulo, en el orden del menú.
 * Cada cambio guarda `home_shortcuts` con `updateMyNavigationPreferences`; «Restablecer» manda `null`.
 */

export const CASILLAS = 6;
export const SUGERIDOS = 4;

export interface GrupoAccesos {
    clave: string;
    titulo: string;
    accesos: AccesoDeInicio[];
}

const ordenar = (a: AccesoDeInicio, b: AccesoDeInicio): number => a.order - b.order || a.key.localeCompare(b.key);

/**
 * Los que se pueden poner en «Tus accesos» (sin los botones `feature`), por su orden. Los de un bloque sin contratar
 * (`not_contracted`, G963) no se ofrecen, como en «Más» del menú; si ya estaba elegido, sale con «+».
 */
export function elegibles(navegacion: Pick<Navegacion, 'shortcuts'>): AccesoDeInicio[] {
    return navegacion.shortcuts.filter((a) => a.kind !== 'feature' && !a.not_contracted).sort(ordenar);
}

/** El cajón: «Sugeridos para ti» y, después, por la categoría del menú del módulo que declara cada acceso. */
export function gruposDelCajon(navegacion: Pick<Navegacion, 'shortcuts' | 'items'>, elegidos: readonly string[]): GrupoAccesos[] {
    const todos = elegibles(navegacion);
    const sugeridos = todos.filter((a) => !elegidos.includes(a.key)).slice(0, SUGERIDOS);
    const grupoDe = (modulo: string): string => navegacion.items.find((i) => i.module === modulo && i.area === 'menu' && i.group != null)?.group ?? '';
    const orden: string[] = [];
    for (const i of navegacion.items) {
        if (i.area === 'menu' && i.group != null && !orden.includes(i.group)) {
            orden.push(i.group);
        }
    }
    const porGrupo = new Map<string, AccesoDeInicio[]>();
    for (const a of todos) {
        const g = grupoDe(a.module);
        porGrupo.set(g, [...(porGrupo.get(g) ?? []), a]);
    }
    const claves = [...porGrupo.keys()].sort((a, b) => (a === '' ? 1 : b === '' ? -1 : orden.indexOf(a) - orden.indexOf(b)));
    return [
        ...(sugeridos.length > 0 ? [{ clave: 'sugeridos', titulo: 'Sugeridos para ti', accesos: sugeridos }] : []),
        ...claves.map((g) => ({ clave: g === '' ? 'otros' : g, titulo: g === '' ? 'Otros' : tituloGrupo(g), accesos: porGrupo.get(g) ?? [] })),
    ];
}

/**
 * Pone un acceso en una casilla. Si ya estaba en otra, las dos se cambian de sitio (nunca sale dos veces); con menos de
 * seis, una casilla vacía lo añade al final.
 */
export function ponerEnCasilla(claves: readonly string[], indice: number, clave: string): string[] {
    const nuevas = [...claves];
    const antes = nuevas.indexOf(clave);
    if (antes === indice) {
        return nuevas;
    }
    if (indice >= nuevas.length) {
        return antes >= 0 ? nuevas : [...nuevas, clave].slice(0, CASILLAS);
    }
    const saliente = nuevas[indice] ?? clave;
    nuevas[indice] = clave;
    if (antes >= 0) {
        nuevas[antes] = saliente;
    }
    return nuevas;
}

/** Cambia de sitio dos casillas. */
export function moverCasilla(claves: readonly string[], desde: number, hasta: number): string[] {
    const nuevas = [...claves];
    const a = nuevas[desde];
    const b = nuevas[hasta];
    if (a === undefined || b === undefined || desde === hasta) {
        return nuevas;
    }
    nuevas[desde] = b;
    nuevas[hasta] = a;
    return nuevas;
}
